import React, { useEffect, useState } from "react";
import PublicGrid from "@/components/PublicGrid";
import ProjectCard from "@/components/ProjectCard";
import InvestmentCard from "@/components/InvestmentCard";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Sparkles, LogOut, Settings } from "lucide-react";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";


export default function Projects() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const startRename = (p) => { setEditing(p); setName(p.name); setDesc(p.description || ""); setOpen(true); };
  const closeDialog = () => { setOpen(false); setEditing(null); setName(""); setDesc(""); };

  const load = async () => {
    try { const { data } = await api.get("/projects"); setProjects(data); }
    catch(e){ toast.error("Failed to load projects"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const createProject = async () => {
    if (!name.trim()) { toast.error("Give your project a name"); return; }
    setCreating(true);
    if (editing) {
      try {
        const { data } = await api.patch(`/projects/${editing.id}`, { name, description: desc });
        setProjects((p) => p.map((x) => (x.id === data.id ? { ...x, name: data.name, description: data.description } : x)));
        toast.success("Project renamed");
        closeDialog();
      } catch (e) { toast.error(e?.response?.data?.detail || "Could not rename"); }
      finally { setCreating(false); }
      return;
    }
    try {
      const { data } = await api.post("/projects", { name, description: desc });
      toast.success("Project created");
      setOpen(false); setName(""); setDesc("");
      setProjects((p)=>[data, ...p]);
      nav(`/studio/${data.id}/image`);
    } catch(e){ toast.error("Failed to create"); }
    finally { setCreating(false); }
  };

  const removeProject = async (id) => {
    if (!window.confirm("Delete this project and all its generations?")) return;
    try {
      await api.delete(`/projects/${id}`);
      setProjects((p)=>p.filter(x=>x.id!==id));
      toast.success("Deleted");
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="min-h-screen bg-transparent text-[#F5F5F7] relative z-[1] overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[#0B1013]/60 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={34} />
            <span className="ax-pill ml-3">Projects</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-xs font-mono text-zinc-500 uppercase tracking-widest" data-testid="projects-user-display">
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"/> {user?.display}
            </div>
            <Button data-testid="projects-settings-button" variant="ghost" size="sm" onClick={()=>nav("/settings")} title="Settings" className="text-zinc-400 hover:text-white">
              <Settings className="w-4 h-4"/>
            </Button>
            <Button data-testid="projects-logout-button" variant="ghost" size="sm" onClick={logout} className="text-zinc-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-1"/> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-6 pt-14 pb-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 ax-fade-up">
          <div>
            <div className="ax-pill mb-4">Welcome back, {user?.display}</div>
            <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tight leading-[1.02]">
              Pick the <span className="ax-gradient-text">Project</span>,<br/>or start a new one.
            </h1>
            <p className="text-zinc-400 mt-5 max-w-xl leading-relaxed">
              Every project keeps its own prompts, models, references and history —
              so a film teaser never crosses paths with a product campaign.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 self-start md:self-auto">
          <InvestmentCard refreshKey={projects.map((p) => p.id).join(",")} onOpenProject={(id) => nav(`/studio/${id}/image`)} />
          <Button
            onClick={()=>setOpen(true)}
            data-testid="projects-create-button"
            className="btn-primary-glow h-12 px-6 text-white font-semibold self-center"
          >
            <Plus className="w-4 h-4 mr-2"/> New Project
          </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-zinc-500 font-mono text-sm">loading projects…</div>
        ) : projects.length === 0 ? (
          <div className="ax-card p-12 text-center max-w-2xl mx-auto" data-testid="projects-empty-state">
            <div className="h-14 w-14 mx-auto rounded-2xl btn-primary-glow flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white"/>
            </div>
            <h3 className="font-heading text-2xl font-semibold mt-6">No projects yet</h3>
            <p className="text-zinc-500 mt-2">Create your first studio to start generating.</p>
            <Button onClick={()=>setOpen(true)} data-testid="projects-empty-create-button" className="mt-6 btn-primary-glow text-white">
              <Plus className="w-4 h-4 mr-2"/> Create first project
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {projects.map((p) => (
              <ProjectCard key={p.id} p={p} onOpen={() => nav(`/studio/${p.id}/image`)} onRename={() => startRename(p)} onDelete={() => removeProject(p.id)} />
            ))}
          </div>
        )}

        <section className="mt-20" data-testid="projects-showcase">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="ax-pill mb-3">Community showcase</div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight">Recently <span className="ax-gradient-text">published</span></h2>
            </div>
            <span className="hidden sm:block text-xs text-zinc-500">Publish any result from the studio to show it here</span>
          </div>
          <PublicGrid emptyHint="Turn on Publish for any finished result in the studio and it will appear here." />
        </section>
      </main>

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : closeDialog())}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-[#F5F5F7]" data-testid="project-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? "Edit project" : "Create new project"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Project name</label>
              <Input
                data-testid="project-name-input"
                value={name}
                onChange={(e)=>setName(e.target.value)}
                placeholder="Film Teaser 2026"
                className="mt-2 bg-[#0A0A0A] border-zinc-800 h-11"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Short description (optional)</label>
              <Textarea
                data-testid="project-desc-input"
                value={desc}
                onChange={(e)=>setDesc(e.target.value)}
                rows={3}
                placeholder="Cinematic sci-fi teaser pack"
                className="mt-2 bg-[#0A0A0A] border-zinc-800"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={closeDialog} data-testid="project-cancel-button">Cancel</Button>
            <Button onClick={createProject} disabled={creating} data-testid="project-submit-button" className="btn-primary-glow text-white">
              {creating ? (editing ? "Saving…" : "Creating…") : (editing ? "Save" : "Create Project")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
